import React from "react";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ButtonNoShadow } from "../styled/button";

// ANIMATED MODAL COMPONENTS
import {
  AnimatedModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "./AnimatedModal";

// ICONS
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import WarningIcon from "@mui/icons-material/Warning";

// COLORS
import { grey, red } from "@mui/material/colors";

/**
 * Enhanced Remove Modal Component using AnimatedModal system
 * Follows Single Responsibility Principle - handles only removal confirmation
 */
export default function RemoveModal({
  show,
  title,
  message,
  removeLabel = "Deletar",
  toggleModal,
  onRemove,
}) {
  // Event Handlers - Separated for clarity and testability
  const handleCloseModal = (event) => {
    toggleModal && toggleModal({}, event);
  };

  const handleRemoveAction = () => {
    onRemove();
    handleCloseModal();
  };

  // Render Methods - Following vertical structure
  const renderModalHeader = () => (
    <ModalHeader
      title={title}
      icon={WarningIcon}
      onClose={handleCloseModal}
      customContent={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <WarningIcon sx={{ color: red[700], mr: 1.5 }} />
          <Typography variant="h6" fontWeight="bold" sx={{ color: red[700] }}>
            {title}
          </Typography>
        </Box>
      }
    />
  );

  const renderModalContent = () => (
    <ModalContent padding={3}>
      <Typography
        variant="subtitle1"
        align="center"
        sx={{
          color: grey[800],
          lineHeight: 1.6,
        }}
      >
        {message}
      </Typography>
    </ModalContent>
  );

  const renderActionButtons = () => (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        gap: 0,
      }}
    >
      <ButtonNoShadow
        width="50%"
        size="large"
        startIcon={<KeyboardBackspaceIcon />}
        fontWeight="bold"
        backgroundColor={grey[300]}
        borderBottomRightRadius={0}
        borderTopRightRadius={0}
        onClick={handleCloseModal}
      >
        Voltar
      </ButtonNoShadow>

      <ButtonNoShadow
        width="50%"
        size="large"
        startIcon={<DeleteIcon />}
        textColor={grey[50]}
        fontWeight="bold"
        backgroundColor={red[500]}
        backgroundColorHover={red[400]}
        borderBottomLeftRadius={0}
        borderTopLeftRadius={0}
        onClick={handleRemoveAction}
      >
        {removeLabel}
      </ButtonNoShadow>
    </Box>
  );

  const renderModalFooter = () => (
    <ModalFooter borderTop={false}>{renderActionButtons()}</ModalFooter>
  );

  // Main Render - Clean and readable
  return (
    <AnimatedModal
      isOpen={show}
      onClose={handleCloseModal}
      width="400px"
      maxWidth="90vw"
      maxHeight="300px"
      zIndex={9999999}
      forceOnTop={true}
    >
      {renderModalHeader()}
      {renderModalContent()}
      {renderModalFooter()}
    </AnimatedModal>
  );
}
